import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Home, Search, Download, Printer } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface DoctorWiseItem {
  doctor_id: string;
  doctor_name: string;
  specialization: string;
  total_patients: number;
  total_revenue: number;
  total_consultations: number;
  commission_earned: number;
}

export default function DoctorWiseReport() {
  const { formatCurrency } = useCurrency();
  const [reportData, setReportData] = useState<DoctorWiseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadDoctorWiseReport();
  }, [startDate, endDate]);

  const loadDoctorWiseReport = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setReportData(getMockDoctorWiseReport());
        setLoading(false);
        return;
      }

      let query = supabase
        .from('invoices')
        .select(`
          id,
          total,
          issue_date,
          doctor_id,
          doctor:doctors(id, name, specialization)
        `)
        .eq('reference_type', 'opd')
        .not('doctor_id', 'is', null);

      if (startDate) {
        query = query.gte('issue_date', startDate);
      }
      if (endDate) {
        query = query.lte('issue_date', endDate);
      }

      const { data, error } = await query;
      if (error) throw error;

      const grouped = (data || []).reduce((acc: Record<string, any>, item) => {
        const doctorId = item.doctor_id;
        if (!acc[doctorId]) {
          acc[doctorId] = {
            doctor_id: doctorId,
            doctor_name: item.doctor?.name || 'N/A',
            specialization: item.doctor?.specialization || 'N/A',
            total_patients: 0,
            total_revenue: 0,
            total_consultations: 0,
            commission_earned: 0,
          };
        }
        
        acc[doctorId].total_patients += 1;
        acc[doctorId].total_revenue += item.total;
        acc[doctorId].total_consultations += 1;
        acc[doctorId].commission_earned += item.total * 0.1;
        
        return acc;
      }, {});

      setReportData(Object.values(grouped));
    } catch (error) {
      console.error('Error loading doctor wise report:', error);
      setReportData(getMockDoctorWiseReport());
    } finally {
      setLoading(false);
    }
  };

  const getMockDoctorWiseReport = (): DoctorWiseItem[] => {
    return [
      {
        doctor_id: '1',
        doctor_name: 'Dr. Ahmed Rahman',
        specialization: 'Cardiology',
        total_patients: 45,
        total_revenue: 225000,
        total_consultations: 45,
        commission_earned: 22500,
      },
      {
        doctor_id: '2',
        doctor_name: 'Dr. Fatima Khan',
        specialization: 'Pediatrics',
        total_patients: 38,
        total_revenue: 190000,
        total_consultations: 38,
        commission_earned: 19000,
      },
      {
        doctor_id: '3',
        doctor_name: 'Dr. Rahman Ali',
        specialization: 'Orthopedics',
        total_patients: 52,
        total_revenue: 260000,
        total_consultations: 52,
        commission_earned: 26000,
      },
    ];
  };

  const filteredData = reportData.filter(item => {
    const matchesSearch = 
      item.doctor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.specialization.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalRevenue = filteredData.reduce((sum, item) => sum + item.total_revenue, 0);
  const totalPatients = filteredData.reduce((sum, item) => sum + item.total_patients, 0);
  const totalCommission = filteredData.reduce((sum, item) => sum + item.commission_earned, 0);

  const handleExportCSV = () => {
    const headers = ['Doctor Name', 'Specialization', 'Total Patients', 'Total Consultations', 'Total Revenue', 'Commission Earned'];
    const rows = filteredData.map(item => [
      item.doctor_name,
      item.specialization,
      item.total_patients,
      item.total_consultations,
      item.total_revenue,
      item.commission_earned,
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `doctor-wise-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Doctor Wise Report</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Doctors</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{filteredData.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Patients</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{totalPatients}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Revenue</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalRevenue)}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search doctor, specialization..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Doctor Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Specialization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Patients
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Consultations
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Revenue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commission
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      No doctor data found
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.doctor_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.doctor_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {item.specialization}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.total_patients}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.total_consultations}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-bold">
                        {formatCurrency(item.total_revenue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-bold">
                        {formatCurrency(item.commission_earned)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
